// /src/pages/ResetPasswordPage.tsx
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Paper, Typography, Box, TextField, Button, CircularProgress, Alert, Stack } from '@mui/material';
import { resetPassword } from '../services/authService';

export const ResetPasswordPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 12 || password.length > 256) {
      setError('Use a password between 12 and 256 characters.');
      return;
    }
    if (!token) {
      setError('Invalid reset link. Please request a new one.');
      return;
    }
    setLoading(true);
    try {
      await resetPassword(token.trim(), password);
      setSuccess('Your password has been reset successfully! Redirecting to login...');
      setTimeout(() => navigate('/login'), 3000);
    } catch (err: any) {
      // --- THIS IS THE INTEGRATED FIX ---
      if (err.response && err.response.status === 429) {
        setError(err.response.data.message || 'Too many attempts. Please try again later.');
      } else {
        setError(err.response?.data?.message || 'Failed to reset password. The link may be invalid or expired.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Paper elevation={6} sx={{ marginTop: 8, p: 4, borderRadius: 0 }}>
        <Typography component="h1" variant="h5" sx={{ mb: 2 }}>
          Set a New Password
        </Typography>
        <Box component="form" onSubmit={handleSubmit}>
          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 0 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 0 }}>{success}</Alert>}
          {!success && (
            <Stack spacing={2}>
              <TextField
                required
                fullWidth
                autoFocus
                label="New Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                helperText="Use at least 12 characters."
                error={password.length > 0 && (password.length < 12 || password.length > 256)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
              />
              <TextField
                required
                fullWidth
                label="Confirm New Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                error={confirmPassword.length > 0 && password !== confirmPassword}
                helperText={confirmPassword.length > 0 && password !== confirmPassword ? "Passwords do not match" : ""}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
              />
              <Button type="submit" fullWidth variant="contained" sx={{ mt: 2, borderRadius: 0 }} disabled={loading}>
                {loading ? <CircularProgress size={24} /> : 'Reset Password'}
              </Button>
            </Stack>
          )}
        </Box>
      </Paper>
    </Container>
  );
};
